"use client";

import { Button, Heading, HStack, Text, VStack } from "@chakra-ui/react";
import {
  PROFILE_SHAPING_EMPTY_STATES,
  PROFILE_SHAPING_LABELS,
  PROFILE_SHAPING_MESSAGES,
} from "@/features/profile-shaping/config/actions";
import { useSelectionState, useUserChoices } from "@/state/globalStore";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import GenericEmptyComponent from "@/shared/ui/GenericEmptyComponent";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import GenericSelector from "@/shared/ui/controls/GenericSelector";
import { Target } from "@/generated/fastapi";
import {
  getTargetDisplayLabel,
  useTargetItems,
  useTargetsQuery,
} from "@/features/targets";

type RerankSelectionProps = {
  onDone?: () => void;
};

export default function RerankSelection({ onDone }: RerankSelectionProps) {
  const targetsQuery = useTargetsQuery();
  const targetItems = useTargetItems(targetsQuery.data);
  const { targetsToRerank, setTargetsToRerank, setInterimState } =
    useUserChoices();
  const { countSelectedThoughts } = useSelectionState();
  const selectedFocus = targetsToRerank[0];
  const hasFocuses = (targetsQuery.data?.length ?? 0) > 0;
  const selectedThoughtsCount = countSelectedThoughts();
  const selectedThoughtsLabel = `${selectedThoughtsCount} ${
    selectedThoughtsCount === 1 ? "thought" : "thoughts"
  }`;
  const selectedFocusLabel = selectedFocus
    ? getTargetDisplayLabel(selectedFocus)
    : PROFILE_SHAPING_LABELS.chooseFocus;

  return (
    <VStack spaceY="4" align="stretch">
      <GenericDrawerInfo
        description={PROFILE_SHAPING_MESSAGES.tailorToFocus}
        items={[
          { label: "Order mode", colorPalette: "blue" },
          {
            label: selectedThoughtsLabel,
            colorPalette: selectedThoughtsCount > 0 ? "green" : "gray",
          },
          {
            label: selectedFocusLabel,
            colorPalette: selectedFocus ? "purple" : "gray",
          },
        ]}
      />

      <VStack spaceY="2" align="stretch">
        <VStack spaceY="1" align="stretch">
          <Heading size="sm">{PROFILE_SHAPING_LABELS.profileFocus}</Heading>
          <Text fontSize="sm" color="moveon.text.muted">
            Choose the focus lens used to order selected thoughts by reader
            relevance.
          </Text>
        </VStack>

        <GenericSelector<Target>
          key={JSON.stringify(targetItems)}
          previewItems={targetItems}
          stateObject={targetsToRerank}
          stateObjectSetter={setTargetsToRerank}
          sourceItems={targetsQuery.data ?? []}
          mode="select"
          width="full"
          isLoading={targetsQuery.isLoading || targetsQuery.isFetching}
          showDescription
          placeholder={
            hasFocuses
              ? PROFILE_SHAPING_LABELS.chooseFocus
              : PROFILE_SHAPING_EMPTY_STATES.profileFocus.title
          }
          allowMultiple={false}
        />
      </VStack>

      {!hasFocuses && !targetsQuery.isLoading ? (
        <GenericEmptyComponent
          title={PROFILE_SHAPING_EMPTY_STATES.profileFocus.title}
          message={PROFILE_SHAPING_EMPTY_STATES.profileFocus.message}
          icon="profile_focus"
          variant="inline"
        />
      ) : null}

      {!selectedFocus && hasFocuses ? (
        <Text fontSize="xs" color="moveon.text.muted">
          Choose a focus lens to guide the current order. You can also keep
          manual ordering and close this dialog.
        </Text>
      ) : null}

      <HStack gap="2" flexWrap="wrap" justify="space-between">
        <Button
          bg="moveon.primary"
          onClick={onDone}
          flex={{ base: "1 1 100%", sm: "0 0 auto" }}
        >
          <GenericIcon icon="confirm" />
          Done
        </Button>

        <HStack gap="2" flexWrap="wrap" justify="flex-end">
          <Button
            variant="outline"
            onClick={() => setInterimState("add_target", { isOpen: true })}
          >
            <GenericIcon icon="profile_focus" />
            {PROFILE_SHAPING_LABELS.addFocus}
          </Button>
          <Button
            variant="ghost"
            disabled={!selectedFocus}
            title={
              selectedFocus
                ? PROFILE_SHAPING_LABELS.editFocus
                : "Choose a focus lens before editing"
            }
            onClick={() => {
              if (!selectedFocus) return;

              setInterimState("edit_target", {
                isOpen: true,
                passedObject: selectedFocus,
              });
            }}
          >
            <GenericIcon icon="edit" />
            {PROFILE_SHAPING_LABELS.editFocus}
          </Button>
        </HStack>
      </HStack>

      {!selectedFocus && hasFocuses ? (
        <Text fontSize="xs" color="moveon.text.muted" textAlign="right">
          Edit becomes available after choosing a focus lens.
        </Text>
      ) : null}
    </VStack>
  );
}
